import {
    Autocomplete,
    Box,
    FormControl,
    FormHelperText,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { ReactElement, useMemo, useState } from 'react'
import TruncatedText from './TruncatedText'
import { AddRounded } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

export interface SelectItem {
    id: string
    name: string
    [key: string]: any
}

interface SearchSelectInputProps {
    id: string
    label: string
    value: string
    placeholder?: string
    onSelect: (id: string, value: string) => void
    list: SelectItem[]
    required?: boolean
    disabled?: boolean
    isLegend?: boolean
    list_Height?: string
    hasAddNew?: boolean
    onClickAddNew?: () => void
    onClickRowIcon?: (item: SelectItem) => void
    hasEmpty?: boolean
    errorMessage?: string
}

const SearchAndSelectInput = ({
    id,
    label,
    value,
    placeholder,
    onSelect,
    list,
    required = false,
    disabled = false,
    isLegend = false,
    list_Height = '250px',
    hasAddNew = false, // need to implement this
    onClickAddNew = () => {}, // need to implement this
    onClickRowIcon, // need to implement this
    hasEmpty = true,
    errorMessage = '',
}: SearchSelectInputProps): ReactElement => {
    const { t } = useTranslation()
    const [inputValue, setInputValue] = useState('')

    const addNewOption = {
        id: 'ADD_NEW',
        name: '',
        hasItemEdit: false,
    }
    const fullList = useMemo(
        () =>
            hasAddNew
                ? [
                      addNewOption,
                      ...(hasEmpty ? [{ id: '', name: '' }] : []),
                      ...(list || []),
                  ]
                : hasEmpty
                  ? [{ id: '', name: '' }, ...(list || [])]
                  : list,
        [list, hasEmpty, hasAddNew],
    )
    const selectedObject = useMemo(
        () =>
            fullList.find((item) => item.id === value) || {
                id: '',
                name: '',
            },
        [fullList, value],
    )

    const handleInputChange = (_event: any, newInputValue: string) => {
        setInputValue(newInputValue)
    }

    const handleChange = (_event: any, newValue: SelectItem | null) => {
        onSelect(id, newValue?.id || '')
    }

    const handleRowIcon = (e: any, item: any) => {
        e.stopPropagation()
        onClickRowIcon?.(item)
    }

    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth disabled={disabled} size="small">
                {!isLegend && (
                    <Typography sx={{ opacity: 0.7, fontSize: 14 }}>
                        {label}
                        {required && '*'}
                    </Typography>
                )}

                <Autocomplete
                    id={`${id}-select`}
                    options={fullList}
                    value={selectedObject}
                    inputValue={inputValue}
                    getOptionLabel={(option) => option.name || ''}
                    onInputChange={handleInputChange}
                    onChange={handleChange}
                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                    disableClearable
                    disabled={disabled}
                    slotProps={{
                        listbox: {
                            style: { maxHeight: list_Height },
                        },
                        popupIndicator: {
                            id: `${id}-dropdown-icon`,
                        },
                    }}
                    noOptionsText={''}
                    filterOptions={(options, state) => {
                        const filtered = options.filter(
                            (option) =>
                                option.id === 'ADD_NEW' ||
                                option.name
                                    .toLowerCase()
                                    .includes(state.inputValue.toLowerCase()),
                        )
                        return filtered
                    }}
                    renderOption={(props, option) =>
                        option.id === 'ADD_NEW' ? (
                            <Box
                                component="li"
                                id={id + 'add_new'}
                                key="add_new"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    cursor: 'pointer',
                                    padding: '2px 10px',
                                    '& :hover': {
                                        background: '#f9f9f9',
                                    },
                                }}
                                onClick={() => onClickAddNew()}
                            >
                                <AddRounded color={'primary'} />
                                <Box
                                    sx={{
                                        marginLeft: '5px',
                                    }}
                                >
                                    <Typography
                                        fontWeight="600"
                                        fontSize="14px"
                                    >
                                        {t('addNew')}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                component="li"
                                {...props}
                                key={option.id}
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    '&:hover': {
                                        '& > .row-icon': {
                                            display: 'flex',
                                        },
                                    },
                                }}
                            >
                                <TruncatedText
                                    text={
                                        option.id === ''
                                            ? '---select---'
                                            : option.name
                                    }
                                    sx={{
                                        fontSize: 14,
                                        opacity: option.id === '' ? 0.4 : 1,
                                    }}
                                />
                                {option.hasItemEdit && option.id !== '' && (
                                    <IconButton
                                        id={`${option.name}-edit-icon`}
                                        onClick={(e) =>
                                            handleRowIcon(e, option)
                                        }
                                        aria-haspopup="true"
                                        key="edit"
                                        disabled={false}
                                        sx={{
                                            padding: '0px',
                                            marginLeft: 'auto',
                                            display: 'none',
                                        }}
                                        className="row-icon"
                                    >
                                        <img
                                            src="/assets/images/icons/edit.svg"
                                            alt="Edit"
                                        />
                                    </IconButton>
                                )}
                            </Box>
                        )
                    }
                    renderInput={(params) => (
                        <Tooltip
                            title={selectedObject?.name || ''}
                            arrow
                            placement="top"
                        >
                            <TextField
                                {...params}
                                label={
                                    isLegend
                                        ? `${label}${required ? ' *' : ''}`
                                        : ''
                                }
                                size="small"
                                placeholder={
                                    (!selectedObject ||
                                        selectedObject.id === '') &&
                                    placeholder
                                        ? placeholder
                                        : t('selectLabel', { label })
                                }
                                error={!!errorMessage}
                                InputProps={{
                                    ...params.InputProps,
                                    style: {
                                        fontSize: 14,
                                        boxShadow: 'none',
                                    },
                                }}
                            />
                        </Tooltip>
                    )}
                    sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            padding: '2.5px 4px 2.5px 5px',
                            '& input::placeholder': {
                                opacity: 0.2,
                            },
                        },
                        '& > div > div': {
                            paddingRight: '28px !important',
                        },
                    }}
                />

                {errorMessage && (
                    <FormHelperText sx={{ color: 'red' }}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    )
}

export default SearchAndSelectInput
